import React, { useRef, useState } from 'react';
import { SparkIcon, TooltipTrigger } from '@spark';
import TagInput, { appendUnique, splitRawItems } from '@/shared/components/TagInput';

const LABELS_FILE_ACCEPT = '.txt,.csv,text/plain,text/csv';

const isLabelsFile = (file) => /\.(txt|csv)$/i.test(file?.name || '');

/**
 * Tag input for the "Labels in this task" sections (Figma 3099-9289).
 * Keyboard/tag behavior lives in shared TagInput; this wrapper adds .txt/.csv
 * upload via the attach icon or by dropping a file onto the field.
 *
 * `readOnly` renders the labels as display-only chips (no input, no upload).
 */
const LabelsTagInput = ({
    labels = [],
    onChange,
    placeholder = 'Type/paste labels separated with commas',
    readOnly = false,
}) => {
    const [fileError, setFileError] = useState(null);
    const fileInputRef = useRef(null);

    const readLabelsFile = async (file) => {
        if (!file) return;
        if (!isLabelsFile(file)) {
            setFileError('Only .txt or .csv files are supported');
            return;
        }
        try {
            const text = await file.text();
            const next = appendUnique(labels, splitRawItems(text));
            if (next.length !== labels.length) onChange(next);
            setFileError(null);
        } catch {
            setFileError(`Could not read "${file.name}"`);
        }
    };

    const handleFileInputChange = async (e) => {
        await readLabelsFile(e.target.files?.[0]);
        e.target.value = '';
    };

    return (
        <TagInput
            values={labels}
            onChange={onChange}
            placeholder={placeholder}
            ariaLabel="Task labels"
            wrapClassName="cw-labels-input-wrap"
            error={fileError}
            readOnly={readOnly}
            onFileDrop={readLabelsFile}
            suffix={(
                <>
                    <TooltipTrigger text="Upload comma-separated labels (.csv or .txt)">
                        <button
                            type="button"
                            className="tag-input__attach"
                            aria-label="Upload labels file"
                            onClick={(e) => {
                                e.stopPropagation();
                                fileInputRef.current?.click();
                            }}
                        >
                            <SparkIcon slug="attach" size="small" />
                        </button>
                    </TooltipTrigger>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={LABELS_FILE_ACCEPT}
                        className="tag-input__file-input"
                        onChange={handleFileInputChange}
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                </>
            )}
        />
    );
};

export default LabelsTagInput;
